import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { SubmissionFormField, SubmissionFormFieldRequest } from '@/types/generated'

interface FieldValue {
  value: string
  signed_at: string | null // ISO timestamp for signature fields (null if not signed)
  type: string // field type (text, textarea, date, checkbox, signature)
}

interface FieldValuesMap {
  [fieldUuid: string]: FieldValue
}

export const usePdfFormSubmissionStore = defineStore('pdfFormSubmission', () => {
  const fieldValues = ref<FieldValuesMap>({})
  const submissionUuid = ref<string | null>(null)
  const formFields = ref<Array<SubmissionFormField>>([])

  const getStorageKey = (uuid: string) => `pdf_form_submission_${uuid}`

  const loadFromStorage = (uuid: string): FieldValuesMap | null => {
    try {
      const stored = sessionStorage.getItem(getStorageKey(uuid))
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error loading from sessionStorage:', error)
    }
    return null
  }

  const saveToStorage = () => {
    if (!submissionUuid.value) return
    try {
      sessionStorage.setItem(getStorageKey(submissionUuid.value), JSON.stringify(fieldValues.value))
    } catch (error) {
      console.error('Error saving to sessionStorage:', error)
    }
  }

  const initializeSubmission = (uuid: string, fields: Array<SubmissionFormField>): void => {
    if (submissionUuid.value && submissionUuid.value !== uuid) {
      fieldValues.value = {}
    }

    submissionUuid.value = uuid
    formFields.value = fields

    const stored = loadFromStorage(uuid)
    if (stored) {
      fieldValues.value = stored
    } else {
      fieldValues.value = {}
      fields.forEach((field) => {
        if (field.uuid) {
          fieldValues.value[field.uuid] = {
            value: field.value || '',
            signed_at: field.signed_at || null,
            type: field.type
          }
        }
      })
      saveToStorage()
    }
  }

  const updateFieldValue = (fieldUuid: string, value: any, fieldType: string): void => {
    if (!fieldUuid) return

    if (fieldType === 'checkbox') {
      value = value ? 'True' : 'False'
    }

    fieldValues.value[fieldUuid] = {
      value,
      signed_at: fieldType === 'signature' ? new Date().toISOString() : null,
      type: fieldType
    }

    saveToStorage()
  }

  const updateSignatureField = (fieldUuid: string, signatureData: string): void => {
    if (!fieldUuid) return

    fieldValues.value[fieldUuid] = {
      value: signatureData,
      signed_at: new Date().toISOString(),
      type: 'signature'
    }

    saveToStorage()
  }

  const getFieldValue = (fieldUuid: string | undefined): string => {
    if (!fieldUuid) return ''
    return fieldValues.value[fieldUuid]?.value ?? ''
  }

  const getSignedAt = (fieldUuid: string | undefined): string | null => {
    if (!fieldUuid) return null
    return fieldValues.value[fieldUuid]?.signed_at || null
  }

  const getFormData = (): Array<SubmissionFormFieldRequest> => {
    return formFields.value.map((field) => {
      const fieldValue = fieldValues.value[field.uuid || '']

      return {
        uuid: field.uuid,
        type: field.type,
        tag: field.tag,
        tag_relationship: field.tag_relationship,
        placeholder: field.placeholder,
        required: field.required,
        assigned_to: field.assigned_to,
        coordinates: {
          x: field.coordinates.x,
          y: field.coordinates.y,
          width: field.coordinates.width,
          height: field.coordinates.height,
          page: field.coordinates.page
        },
        value: fieldValue?.value ?? null, // preserve "false" and "0" values
        signed_at: fieldValue?.signed_at || null // Null if not signed (backend allows null)
      }
    })
  }

  const syncWithSignatureStore = (
    signatureStore: any,
    uuid: string,
    fields: Array<SubmissionFormField>
  ): void => {
    const signatures = signatureStore.signatures

    fields.forEach((field) => {
      if (field.type === 'signature' && field.uuid) {
        const signatureKey = uuid + field.uuid
        const signature = signatures[signatureKey]

        if (signature?.data_uri) {
          fieldValues.value[field.uuid] = {
            value: signature.data_uri,
            signed_at: new Date().toISOString(),
            type: 'signature'
          }
        }
      }
    })

    saveToStorage()
  }

  const clearSubmission = (): void => {
    if (submissionUuid.value) {
      try {
        sessionStorage.removeItem(getStorageKey(submissionUuid.value))
      } catch (error) {
        console.error('Error clearing from sessionStorage:', error)
      }
    }
    fieldValues.value = {}
    submissionUuid.value = null
    formFields.value = []
  }

  return {
    fieldValues,
    submissionUuid,
    initializeSubmission,
    updateFieldValue,
    updateSignatureField,
    getFieldValue,
    getSignedAt,
    getFormData,
    syncWithSignatureStore,
    clearSubmission
  }
})
